import React, { useEffect, useState } from "react";
import AdminHeader from "../../components/Admin/AdminHeader";
import { getApiUrl, getApiHeaders } from "../../config/api";
import { auth } from "../../config/firebaseConfig";

const AdminDashboard = () => {
  // Helper function to get auth headers with Firebase token
  const getAuthHeaders = async (additionalHeaders = {}) => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Not authenticated");
    }
    const token = await user.getIdToken();
    return getApiHeaders({
      ...additionalHeaders,
      'Authorization': `Bearer ${token}`
    });
  };
  // Admin password dialog state and handlers
  const [passwordDialog, setPasswordDialog] = useState({ open: false, uid: null, action: null, newRole: null });
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  // When confirmation dialog is confirmed, show password dialog
  const handleConfirm = () => {
    setPasswordDialog({ open: true, uid: confirmDialog.uid, action: confirmDialog.action, newRole: confirmDialog.newRole });
    setConfirmDialog({ open: false, uid: null, action: null, message: "", newRole: null });
    setPasswordError("");
    setAdminEmail("");
    setAdminPassword("");
  };

  // Actually perform the action after password validation
  const performAction = async (adminEmailInput, adminPasswordInput) => {
    const { uid, action, newRole } = passwordDialog;
    setPasswordLoading(true);
    setPasswordError("");
    try {
      // Verify admin password via backend
      const verifyRes = await fetch(getApiUrl("api/admin/verify-password"), {
        method: "POST",
        headers: getApiHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ email: adminEmailInput, password: adminPasswordInput })
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok || !verifyData.success) {
        setPasswordError(verifyData.detail || "Invalid admin credentials");
        setPasswordLoading(false);
        return;
      }
      // Password valid, proceed with action
      setPasswordDialog({ open: false, uid: null, action: null, newRole: null });
      let endpoint = "";
      let method = "POST";
      let body = null;
      if (action === 'Reset password') endpoint = `api/admin/users/${uid}/reset-password`;
      if (action === 'Disable account') endpoint = `api/admin/users/${uid}/disable`;
      if (action === 'Enable account') endpoint = `api/admin/users/${uid}/enable`;
      if (action === 'Delete account') endpoint = `api/admin/users/${uid}/delete`;
      if (action === 'Change role') {
        endpoint = `api/admin/users/${uid}/role`;
        body = JSON.stringify({ role: newRole });
      }
      if (!endpoint) return;
      const headers = await getAuthHeaders(body ? { "Content-Type": "application/json" } : {});
      const res = await fetch(getApiUrl(endpoint), { 
        method,
        headers,
        ...(body ? { body } : {})
      });
      let resText = await res.text();
      let resJson;
      try { resJson = JSON.parse(resText); } catch { resJson = resText; }
      if (!res.ok) {
        setFeedback({ open: true, type: 'error', message: `Failed to ${action.toLowerCase()} for user ${uid}: ${resJson && resJson.detail ? resJson.detail : resJson}` });
        setPasswordLoading(false);
        return;
      }
      setFeedback({ open: true, type: 'success', message: `${action} successful for user ${uid}` });
      // Refresh user list after action
      const refreshHeaders = await getAuthHeaders();
      const usersRes = await fetch(getApiUrl("api/admin/users"), {
        headers: refreshHeaders
      });
      const usersData = await usersRes.json();
      setAccounts(usersData.users);
    } catch (err) {
      setFeedback({ open: true, type: 'error', message: err.message || `Error performing ${action}` });
    }
    setPasswordLoading(false);
    setAdminEmail("");
    setAdminPassword("");
  };
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("USER");
  const [mainTab, setMainTab] = useState("authentication"); // "authentication" or "notification"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, uid: null, action: null, message: "", newRole: null });
  // Feedback message state
  const [feedback, setFeedback] = useState({ open: false, type: '', message: '' });

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError("");
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(getApiUrl("api/admin/users"), {
          headers
        });
        if (!res.ok) throw new Error("Failed to fetch users");
        const data = await res.json();
        setAccounts(data.users);
      } catch (err) {
        setError(err.message || "Error fetching users");
      }
      setLoading(false);
    };
    fetchUsers();
  }, []);

  const filteredAccounts = accounts.filter(acc => {
    const matchesSearch = acc.email.toLowerCase().includes(search.toLowerCase());
    const matchesTab = acc.role === tab;
    return matchesSearch && matchesTab;
  });

  // Show confirmation dialog instead of window.confirm
  const handleAction = (uid, action) => {
    let confirmMsg = '';
    if (action === 'Reset password') confirmMsg = `Are you sure you want to reset the password for user ${uid}?`;
    if (action === 'Disable account') confirmMsg = `Are you sure you want to disable the account for user ${uid}?`;
    if (action === 'Enable account') confirmMsg = `Are you sure you want to enable the account for user ${uid}?`;
    if (action === 'Delete account') confirmMsg = `Are you sure you want to permanently delete the account for user ${uid}? This cannot be undone.`;
    setConfirmDialog({ open: true, uid, action, message: confirmMsg });
  };


  const handleRoleChange = (uid, newRole) => {
    // Find the user's email for display in confirmation
    const user = accounts.find(acc => acc.uid === uid);
    const confirmMsg = `Are you sure you want to change the role for ${user?.email || uid} to ${newRole}?`;
    setConfirmDialog({ open: true, uid, action: 'Change role', message: confirmMsg, newRole });
  };

  // Add User Dialog state
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState("");

  // Broadcast Notification Dialog state
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [bcTitle, setBcTitle] = useState("");
  const [bcMessage, setBcMessage] = useState("");
  const [bcCategory, setBcCategory] = useState("announcement");
  const [bcActionUrl, setBcActionUrl] = useState("");
  const [bcActionLabel, setBcActionLabel] = useState("");
  const [bcLoading, setBcLoading] = useState(false);
  const [bcError, setBcError] = useState("");

  // Send to Specific User Dialog state
  const [sendUserOpen, setSendUserOpen] = useState(false);
  const [selectedUids, setSelectedUids] = useState([]); // Changed to array for multiple selection
  const [sendUserTitle, setSendUserTitle] = useState("");
  const [sendUserMessage, setSendUserMessage] = useState("");
  const [sendUserCategory, setSendUserCategory] = useState("announcement");
  const [sendUserActionUrl, setSendUserActionUrl] = useState("");
  const [sendUserActionLabel, setSendUserActionLabel] = useState("");
  const [sendUserLoading, setSendUserLoading] = useState(false);
  const [sendUserError, setSendUserError] = useState("");

  const handleAddUser = async () => {
    setAddUserError("");
    if (!newUserEmail || !newUserPassword) {
      setAddUserError("Email and password required");
      return;
    }
    setAddUserLoading(true);
    try {
      const headers = await getAuthHeaders({ "Content-Type": "application/json" });
      const res = await fetch(getApiUrl("api/admin/users/create"), {
        method: "POST",
        headers,
        body: JSON.stringify({ email: newUserEmail, password: newUserPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to add user");
      setFeedback({ open: true, type: 'success', message: `User ${data.email} created!` });
      setAddUserDialog(false);
      setNewUserEmail("");
      setNewUserPassword("");
      // Refresh user list
      const refreshHeaders = await getAuthHeaders();
      const usersRes = await fetch(getApiUrl("api/admin/users"), {
        headers: refreshHeaders
      });
      const usersData = await usersRes.json();
      setAccounts(usersData.users);
    } catch (err) {
      setAddUserError(err.message || "Failed to add user");
    }
    setAddUserLoading(false);
  };

  return (
    <div style={{ background: '#181a20', minHeight: '100vh', color: '#fff', fontFamily: 'Inter, Roboto, Arial, sans-serif' }}>
      <AdminHeader />
      {/* Floating Toast Feedback Message */}
      {feedback.open && (
        <div style={{
          position: 'fixed',
          top: 32,
          right: 32,
          minWidth: 320,
          maxWidth: 400,
          background: feedback.type === 'success' ? 'linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)' : 'linear-gradient(90deg, #ff5858 0%, #f09819 100%)',
          color: '#181a20',
          borderRadius: 10,
          padding: '18px 32px 18px 24px',
          fontWeight: 600,
          fontSize: 16,
          boxShadow: '0 8px 32px #0008',
          zIndex: 20000,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          opacity: feedback.open ? 1 : 0,
          transition: 'opacity 0.3s',
          animation: 'fadeInToast 0.4s',
        }}>
          <span style={{ flex: 1 }}>{feedback.message}</span>
          <button onClick={() => setFeedback({ open: false, type: '', message: '' })} style={{ background: 'none', border: 'none', color: '#181a20', fontWeight: 700, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
          <style>{`
            @keyframes fadeInToast {
              from { opacity: 0; transform: translateY(-20px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
      <div style={{ maxWidth: 1100, margin: '2rem auto', background: '#20232a', borderRadius: 16, boxShadow: '0 4px 24px #0004', padding: 40, border: '1px solid #23272f' }}>
        {/* Main Tabs */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 32, borderBottom: '2px solid #23272f', paddingBottom: 16 }}>
          <button
            onClick={() => setMainTab("authentication")}
            style={{
              background: mainTab === "authentication" ? 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)' : 'transparent',
              color: mainTab === "authentication" ? '#fff' : '#aaa',
              border: 'none',
              borderRadius: 6,
              padding: '12px 36px',
              fontWeight: 700,
              fontSize: 18,
              cursor: 'pointer',
              boxShadow: mainTab === "authentication" ? '0 2px 8px #1976d255' : 'none',
              transition: 'all 0.2s',
              letterSpacing: 0.5
            }}
          >
            Authentication
          </button>
          <button
            onClick={() => setMainTab("notification")}
            style={{
              background: mainTab === "notification" ? 'linear-gradient(90deg, #8E2DE2 0%, #4A00E0 100%)' : 'transparent',
              color: mainTab === "notification" ? '#fff' : '#aaa',
              border: 'none',
              borderRadius: 6,
              padding: '12px 36px',
              fontWeight: 700,
              fontSize: 18,
              cursor: 'pointer',
              boxShadow: mainTab === "notification" ? '0 2px 8px #4A00E055' : 'none',
              transition: 'all 0.2s',
              letterSpacing: 0.5
            }}
          >
            Notifications
          </button>
        </div>

        {/* Authentication Tab Content */}
        {mainTab === "authentication" && (
          <>
            <h1 style={{ marginBottom: 32, fontWeight: 700, fontSize: 32, letterSpacing: 0.5 }}>User Management</h1>
            <div style={{ display: 'flex', gap: 20, marginBottom: 32 }}>
          <button
            onClick={() => setTab("USER")}
            style={{
              background: tab === "USER" ? 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)' : 'transparent',
              color: tab === "USER" ? '#fff' : '#aaa',
              border: tab === "USER" ? 'none' : '1px solid #23272f',
              borderRadius: 6,
              padding: '10px 32px',
              fontWeight: 600,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: tab === "USER" ? '0 2px 8px #1976d255' : 'none',
              transition: 'all 0.2s',
            }}
          >
            USER Management
          </button>
          <button
            onClick={() => setTab("GSO")}
            style={{
              background: tab === "GSO" ? 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)' : 'transparent',
              color: tab === "GSO" ? '#fff' : '#aaa',
              border: tab === "GSO" ? 'none' : '1px solid #23272f',
              borderRadius: 6,
              padding: '10px 32px',
              fontWeight: 600,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: tab === "GSO" ? '0 2px 8px #1976d255' : 'none',
              transition: 'all 0.2s',
            }}
          >
            GSO Management
          </button>
          <button
            onClick={() => setTab("CO-ADMIN")}
            style={{
              background: tab === "CO-ADMIN" ? 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)' : 'transparent',
              color: tab === "CO-ADMIN" ? '#fff' : '#aaa',
              border: tab === "CO-ADMIN" ? 'none' : '1px solid #23272f',
              borderRadius: 6,
              padding: '10px 32px',
              fontWeight: 600,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: tab === "CO-ADMIN" ? '0 2px 8px #1976d255' : 'none',
              transition: 'all 0.2s',
            }}
          >
            CO-ADMIN Management
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <input
            type="text"
            placeholder="Search by email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: 12, borderRadius: 6, border: '1.5px solid #333', width: 320, background: '#23272f', color: '#fff', fontSize: 16, outline: 'none', boxShadow: '0 1px 4px #0002' }}
          />
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              style={{ background: 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 28px', fontWeight: 700, fontSize: 16, boxShadow: '0 2px 8px #1976d255', cursor: 'pointer', letterSpacing: 0.5 }}
              onClick={() => setAddUserDialog(true)}
            >
              Add {tab === "USER" ? "user" : tab === "GSO" ? "GSO" : "CO-ADMIN"}
            </button>
          </div>
        </div>
    {/* Add User Dialog */}
    {addUserDialog && (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }}>
        <div style={{ background: '#23272f', borderRadius: 12, padding: 32, minWidth: 340, boxShadow: '0 4px 24px #0008', color: '#fff', textAlign: 'center' }}>
          <div style={{ fontSize: 20, marginBottom: 18, fontWeight: 600 }}>Add New User</div>
          <input
            type="email"
            placeholder="Email"
            value={newUserEmail}
            onChange={e => setNewUserEmail(e.target.value)}
            style={{ width: '100%', marginBottom: 16, padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181a20', color: '#fff', fontSize: 16, outline: 'none' }}
            autoFocus
          />
          <input
            type="password"
            placeholder="Password"
            value={newUserPassword}
            onChange={e => setNewUserPassword(e.target.value)}
            style={{ width: '100%', marginBottom: 16, padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181a20', color: '#fff', fontSize: 16, outline: 'none' }}
          />
          {addUserError && <div style={{ color: '#ff5858', marginBottom: 12, fontSize: 15 }}>{addUserError}</div>}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18 }}>
            <button
              onClick={handleAddUser}
              disabled={addUserLoading}
              style={{ background: 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 32px', fontWeight: 700, fontSize: 16, cursor: addUserLoading ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px #1976d255' }}
            >
              {addUserLoading ? 'Adding...' : 'Add User'}
            </button>
            <button
              onClick={() => { setAddUserDialog(false); setAddUserError(""); setNewUserEmail(""); setNewUserPassword(""); }}
              disabled={addUserLoading}
              style={{ background: '#23272f', color: '#aaa', border: '1.5px solid #444', borderRadius: 6, padding: '10px 32px', fontWeight: 700, fontSize: 16, cursor: addUserLoading ? 'not-allowed' : 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
        <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #23272f', background: '#23272f', boxShadow: '0 2px 8px #0002' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: 'transparent' }}>
            <thead>
              <tr style={{ background: '#23272f', color: '#90caf9', borderBottom: '2px solid #333', fontSize: 15, fontWeight: 600 }}>
                <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>Identifier</th>
                <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>Providers</th>
                <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>Created</th>
                <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>Signed In</th>
                <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>User UID</th>
                <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>Status</th>
                <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ color: '#90caf9', textAlign: 'center', padding: 40, fontSize: 18 }}>Loading...</td></tr>
              ) : error ? (
                <tr><td colSpan={7} style={{ color: '#f44336', textAlign: 'center', padding: 40, fontSize: 18 }}>{error}</td></tr>
              ) : filteredAccounts.length === 0 ? (
                <tr><td colSpan={7} style={{ color: '#90caf9', textAlign: 'center', padding: 40, fontSize: 18 }}>No accounts found.</td></tr>
              ) : (
                filteredAccounts.map(acc => (
                  <tr 
                    key={acc.uid} 
                    style={{ 
                      borderBottom: '1px solid #222', 
                      background: selectedUids.includes(acc.uid) 
                        ? 'rgba(255, 107, 107, 0.2)' 
                        : 'rgba(25, 118, 210, 0.03)', 
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      outline: selectedUids.includes(acc.uid) ? '2px solid #FF6B6B' : 'none'
                    }}
                    onMouseEnter={e => {
                      if (!selectedUids.includes(acc.uid)) {
                        e.currentTarget.style.background = 'rgba(25, 118, 210, 0.1)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!selectedUids.includes(acc.uid)) {
                        e.currentTarget.style.background = 'rgba(25, 118, 210, 0.03)';
                      }
                    }}
                    onClick={() => {
                      setSelectedUids(prev => 
                        prev.includes(acc.uid) 
                          ? prev.filter(uid => uid !== acc.uid) // Remove if already selected
                          : [...prev, acc.uid] // Add if not selected
                      );
                      if (!sendUserOpen) {
                        setSendUserOpen(true);
                        setSendUserError("");
                      }
                    }}
                    title={selectedUids.includes(acc.uid) ? "Click to deselect" : "Click to select for notification"}
                  >
                    <td style={{ padding: 16 }}>{acc.email}</td>
                    <td style={{ padding: 16 }}>{acc.provider}</td>
                    <td style={{ padding: 16 }}>{acc.createdAt}</td>
                    <td style={{ padding: 16 }}>{acc.lastSignInAt}</td>
                    <td style={{ padding: 16, fontFamily: 'monospace', fontSize: 14 }}>{acc.uid}</td>
                    <td style={{ padding: 16, color: acc.status === 'active' ? '#4caf50' : '#f44336', fontWeight: 600 }}>{acc.status}</td>
                    <td style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 8 }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => handleAction(acc.uid, 'Reset password')} style={{ background: 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)', color: '#fff', border: 'none', borderRadius: 5, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer', boxShadow: '0 1px 4px #1976d255', transition: 'all 0.2s' }}>Reset</button>
                      {acc.status === 'disabled' ? (
                        <button onClick={() => handleAction(acc.uid, 'Enable account')} style={{ background: '#4caf50', color: '#fff', border: 'none', borderRadius: 5, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer', transition: 'all 0.2s' }}>Enable</button>
                      ) : (
                        <button onClick={() => handleAction(acc.uid, 'Disable account')} style={{ background: '#23272f', color: '#ffb300', border: '1.5px solid #ffb300', borderRadius: 5, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer', transition: 'all 0.2s' }}>Disable</button>
                      )}
                      <button onClick={() => handleAction(acc.uid, 'Delete account')} style={{ background: 'linear-gradient(90deg, #b71c1c 60%, #f44336 100%)', color: '#fff', border: 'none', borderRadius: 5, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer', boxShadow: '0 1px 4px #b71c1c55', transition: 'all 0.2s' }}>Delete</button>
                      <select
                        value={acc.role}
                        onChange={e => handleRoleChange(acc.uid, e.target.value)}
                        style={{ marginLeft: 8, background: '#23272f', color: '#fff', border: '1.5px solid #1976d2', borderRadius: 5, padding: '6px 12px', fontWeight: 600, fontSize: 14, cursor: 'pointer', outline: 'none', transition: 'all 0.2s' }}
                      >
                        <option value="USER">USER</option>
                        <option value="GSO">GSO</option>
                        <option value="CO-ADMIN">CO-ADMIN</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          </>
        )}

        {/* Notifications Tab Content */}
        {mainTab === "notification" && (
          <>
            <h1 style={{ marginBottom: 32, fontWeight: 700, fontSize: 32, letterSpacing: 0.5 }}>Notification Center</h1>
            
            {/* Notification Action Buttons */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
              <button
                style={{ 
                  background: selectedUids.length > 0 
                    ? 'linear-gradient(90deg, #FF6B6B 0%, #FFE66D 100%)' 
                    : 'linear-gradient(90deg, #666 0%, #888 100%)', 
                  color: selectedUids.length > 0 ? '#181a20' : '#ccc', 
                  border: 'none', 
                  borderRadius: 6, 
                  padding: '12px 32px', 
                  fontWeight: 700, 
                  fontSize: 16, 
                  boxShadow: selectedUids.length > 0 ? '0 2px 8px #FF6B6B55' : 'none', 
                  cursor: selectedUids.length > 0 ? 'pointer' : 'not-allowed', 
                  letterSpacing: 0.5,
                  transition: 'all 0.2s'
                }}
                onClick={() => { 
                  if (selectedUids.length > 0) {
                    setSendUserOpen(true); 
                    setSendUserError(""); 
                  }
                }}
                disabled={selectedUids.length === 0}
              >
                Send to Selected Users {selectedUids.length > 0 ? `(${selectedUids.length})` : ''}
              </button>
              <button
                style={{ background: 'linear-gradient(90deg, #8E2DE2 0%, #4A00E0 100%)', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 32px', fontWeight: 700, fontSize: 16, boxShadow: '0 2px 8px #4A00E055', cursor: 'pointer', letterSpacing: 0.5 }}
                onClick={() => { setBroadcastOpen(true); setBcError(""); }}
              >
                Broadcast to All
              </button>
            </div>

            {/* Role Tabs */}
            <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
              <button
                onClick={() => setTab("USER")}
                style={{
                  background: tab === "USER" ? 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)' : 'transparent',
                  color: tab === "USER" ? '#fff' : '#aaa',
                  border: tab === "USER" ? 'none' : '1px solid #23272f',
                  borderRadius: 6,
                  padding: '10px 32px',
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: 'pointer',
                  boxShadow: tab === "USER" ? '0 2px 8px #1976d255' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                USER
              </button>
              <button
                onClick={() => setTab("GSO")}
                style={{
                  background: tab === "GSO" ? 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)' : 'transparent',
                  color: tab === "GSO" ? '#fff' : '#aaa',
                  border: tab === "GSO" ? 'none' : '1px solid #23272f',
                  borderRadius: 6,
                  padding: '10px 32px',
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: 'pointer',
                  boxShadow: tab === "GSO" ? '0 2px 8px #1976d255' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                GSO
              </button>
              <button
                onClick={() => setTab("CO-ADMIN")}
                style={{
                  background: tab === "CO-ADMIN" ? 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)' : 'transparent',
                  color: tab === "CO-ADMIN" ? '#fff' : '#aaa',
                  border: tab === "CO-ADMIN" ? 'none' : '1px solid #23272f',
                  borderRadius: 6,
                  padding: '10px 32px',
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: 'pointer',
                  boxShadow: tab === "CO-ADMIN" ? '0 2px 8px #1976d255' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                CO-ADMIN
              </button>
            </div>

            {/* Search Bar */}
            <input
              type="text"
              placeholder="Search by email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: 12, borderRadius: 6, border: '1.5px solid #333', width: '100%', maxWidth: 400, background: '#23272f', color: '#fff', fontSize: 16, outline: 'none', boxShadow: '0 1px 4px #0002', marginBottom: 24 }}
            />

            {/* User Selection Table */}
            <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #23272f', background: '#23272f', boxShadow: '0 2px 8px #0002' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: 'transparent' }}>
                <thead>
                  <tr style={{ background: '#23272f', color: '#90caf9', borderBottom: '2px solid #333', fontSize: 15, fontWeight: 600 }}>
                    <th style={{ padding: 16, textAlign: 'center', letterSpacing: 0.2, width: 60 }}>
                      <input 
                        type="checkbox" 
                        checked={filteredAccounts.length > 0 && selectedUids.length === filteredAccounts.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUids(filteredAccounts.map(acc => acc.uid));
                          } else {
                            setSelectedUids([]);
                          }
                        }}
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                        title="Select/Deselect All"
                      />
                    </th>
                    <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>Email</th>
                    <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>User UID</th>
                    <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>Created</th>
                    <th style={{ padding: 16, textAlign: 'left', letterSpacing: 0.2 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ color: '#90caf9', textAlign: 'center', padding: 40, fontSize: 18 }}>Loading...</td></tr>
                  ) : error ? (
                    <tr><td colSpan={5} style={{ color: '#f44336', textAlign: 'center', padding: 40, fontSize: 18 }}>{error}</td></tr>
                  ) : filteredAccounts.length === 0 ? (
                    <tr><td colSpan={5} style={{ color: '#90caf9', textAlign: 'center', padding: 40, fontSize: 18 }}>No accounts found.</td></tr>
                  ) : (
                    filteredAccounts.map(acc => (
                      <tr 
                        key={acc.uid} 
                        style={{ 
                          borderBottom: '1px solid #222', 
                          background: selectedUids.includes(acc.uid) 
                            ? 'rgba(255, 107, 107, 0.2)' 
                            : 'rgba(25, 118, 210, 0.03)', 
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          outline: selectedUids.includes(acc.uid) ? '2px solid #FF6B6B' : 'none'
                        }}
                        onMouseEnter={e => {
                          if (!selectedUids.includes(acc.uid)) {
                            e.currentTarget.style.background = 'rgba(25, 118, 210, 0.1)';
                          }
                        }}
                        onMouseLeave={e => {
                          if (!selectedUids.includes(acc.uid)) {
                            e.currentTarget.style.background = 'rgba(25, 118, 210, 0.03)';
                          }
                        }}
                        onClick={() => {
                          setSelectedUids(prev => 
                            prev.includes(acc.uid) 
                              ? prev.filter(uid => uid !== acc.uid)
                              : [...prev, acc.uid]
                          );
                        }}
                        title={selectedUids.includes(acc.uid) ? "Click to deselect" : "Click to select for notification"}
                      >
                        <td style={{ padding: 16, textAlign: 'center' }}>
                          <input 
                            type="checkbox" 
                            checked={selectedUids.includes(acc.uid)} 
                            readOnly
                            style={{ width: 18, height: 18, cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: 16 }}>{acc.email}</td>
                        <td style={{ padding: 16, fontFamily: 'monospace', fontSize: 13 }}>{acc.uid}</td>
                        <td style={{ padding: 16 }}>{acc.createdAt}</td>
                        <td style={{ padding: 16, color: acc.status === 'active' ? '#4caf50' : '#f44336', fontWeight: 600 }}>{acc.status}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Send to Specific User Dialog */}
      {sendUserOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{ background: '#23272f', borderRadius: 12, padding: 28, minWidth: 520, maxWidth: 640, boxShadow: '0 4px 24px #0008', color: '#fff' }}>
            <div style={{ fontSize: 20, marginBottom: 12, fontWeight: 700 }}>Send notification to specific user{selectedUids.length !== 1 ? 's' : ''}</div>
            <div style={{ fontSize: 14, marginBottom: 18, color: '#bbb' }}>
              {selectedUids.length > 0 
                ? `Sending to ${selectedUids.length} selected user${selectedUids.length !== 1 ? 's' : ''}. Click users in the table to add/remove them.`
                : 'Click user rows in the table to select recipients (multi-select enabled).'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {selectedUids.length > 0 && (
                <div style={{ 
                  background: '#181a20', 
                  borderRadius: 6, 
                  padding: 12, 
                  border: '1.5px solid #FF6B6B',
                  maxHeight: 120,
                  overflowY: 'auto'
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#FFE66D' }}>
                    Selected UIDs ({selectedUids.length}):
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {selectedUids.map(uid => {
                      const user = accounts.find(acc => acc.uid === uid);
                      return (
                        <div 
                          key={uid}
                          style={{ 
                            background: '#23272f',
                            padding: '4px 10px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontFamily: 'monospace',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            border: '1px solid #FF6B6B'
                          }}
                        >
                          <span title={uid}>{user?.email || uid.substring(0, 12) + '...'}</span>
                          <button
                            onClick={() => setSelectedUids(prev => prev.filter(u => u !== uid))}
                            style={{ 
                              background: 'none',
                              border: 'none',
                              color: '#ff5858',
                              cursor: 'pointer',
                              padding: 0,
                              fontSize: 16,
                              lineHeight: 1,
                              fontWeight: 700
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setSelectedUids([])}
                    style={{
                      marginTop: 8,
                      background: 'none',
                      border: 'none',
                      color: '#ff5858',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: 'underline'
                    }}
                  >
                    Clear all
                  </button>
                </div>
              )}
              <input
                type="text"
                placeholder="Title (optional)"
                value={sendUserTitle}
                onChange={e => setSendUserTitle(e.target.value)}
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #FF6B6B', background: '#181a20', color: '#fff', fontSize: 16, outline: 'none' }}
              />
              <textarea
                placeholder="Message (required)"
                value={sendUserMessage}
                onChange={e => setSendUserMessage(e.target.value)}
                rows={5}
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #FF6B6B', background: '#181a20', color: '#fff', fontSize: 16, outline: 'none', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 12 }}>
                <select
                  value={sendUserCategory}
                  onChange={e => setSendUserCategory(e.target.value)}
                  style={{ flex: 1, background: '#23272f', color: '#fff', border: '1.5px solid #FF6B6B', borderRadius: 6, padding: '10px 12px', fontWeight: 600, fontSize: 14, cursor: 'pointer', outline: 'none' }}
                >
                  <option value="announcement">Announcement</option>
                  <option value="system">System</option>
                  <option value="update">Update</option>
                  <option value="alert">Alert</option>
                  <option value="claim">Claim</option>
                  <option value="match">Match</option>
                  <option value="verification">Verification</option>
                </select>
                <input
                  type="text"
                  placeholder="Action label (optional)"
                  value={sendUserActionLabel}
                  onChange={e => setSendUserActionLabel(e.target.value)}
                  style={{ flex: 1, padding: 12, borderRadius: 6, border: '1.5px solid #FF6B6B', background: '#181a20', color: '#fff', fontSize: 14, outline: 'none' }}
                />
              </div>
              <input
                type="url"
                placeholder="Action URL (optional)"
                value={sendUserActionUrl}
                onChange={e => setSendUserActionUrl(e.target.value)}
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #FF6B6B', background: '#181a20', color: '#fff', fontSize: 14, outline: 'none' }}
              />
              {sendUserError && <div style={{ color: '#ff5858', fontSize: 14 }}>{sendUserError}</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18 }}>
              <button
                onClick={async () => {
                  setSendUserError("");
                  if (selectedUids.length === 0) { setSendUserError("Please select at least one user"); return; }
                  if (!sendUserMessage.trim()) { setSendUserError("Message is required"); return; }
                  setSendUserLoading(true);
                  try {
                    const headers = await getAuthHeaders({ "Content-Type": "application/json" });
                    const res = await fetch(getApiUrl("api/notifications/send"), {
                      method: "POST",
                      headers,
                      body: JSON.stringify({
                        uids: selectedUids,
                        title: sendUserTitle || null,
                        message: sendUserMessage,
                        category: sendUserCategory || "announcement",
                        action_url: sendUserActionUrl || null,
                        action_label: sendUserActionLabel || null,
                      })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) {
                      throw new Error(data?.detail || "Failed to send notification");
                    }
                    const userEmails = data?.users?.map(u => u.email).join(', ') || `${data?.sent_to || selectedUids.length} user(s)`;
                    setFeedback({ open: true, type: 'success', message: `Notification sent to ${userEmails}` });
                    setSendUserOpen(false);
                    setSelectedUids([]); setSendUserTitle(""); setSendUserMessage(""); setSendUserCategory("announcement"); setSendUserActionUrl(""); setSendUserActionLabel("");
                  } catch (e) {
                    setSendUserError(e.message || "Failed to send notification");
                  }
                  setSendUserLoading(false);
                }}
                disabled={sendUserLoading}
                style={{ background: 'linear-gradient(90deg, #FF6B6B 0%, #FFE66D 100%)', color: '#181a20', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 700, fontSize: 16, cursor: sendUserLoading ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px #FF6B6B55' }}
              >
                {sendUserLoading ? 'Sending…' : 'Send notification'}
              </button>
              <button
                onClick={() => { setSendUserOpen(false); setSendUserError(""); setSelectedUids([]); }}
                disabled={sendUserLoading}
                style={{ background: '#23272f', color: '#aaa', border: '1.5px solid #444', borderRadius: 6, padding: '10px 24px', fontWeight: 700, fontSize: 16, cursor: sendUserLoading ? 'not-allowed' : 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Broadcast Notification Dialog */}
      {broadcastOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{ background: '#23272f', borderRadius: 12, padding: 28, minWidth: 420, maxWidth: 560, boxShadow: '0 4px 24px #0008', color: '#fff' }}>
            <div style={{ fontSize: 20, marginBottom: 12, fontWeight: 700 }}>Broadcast notification</div>
            <div style={{ fontSize: 14, marginBottom: 18, color: '#bbb' }}>Send a message to all users. Title is optional; message is required.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="text"
                placeholder="Title (optional)"
                value={bcTitle}
                onChange={e => setBcTitle(e.target.value)}
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181a20', color: '#fff', fontSize: 16, outline: 'none' }}
              />
              <textarea
                placeholder="Message (required)"
                value={bcMessage}
                onChange={e => setBcMessage(e.target.value)}
                rows={5}
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181a20', color: '#fff', fontSize: 16, outline: 'none', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 12 }}>
                <select
                  value={bcCategory}
                  onChange={e => setBcCategory(e.target.value)}
                  style={{ flex: 1, background: '#23272f', color: '#fff', border: '1.5px solid #1976d2', borderRadius: 6, padding: '10px 12px', fontWeight: 600, fontSize: 14, cursor: 'pointer', outline: 'none' }}
                >
                  <option value="announcement">Announcement</option>
                  <option value="system">System</option>
                  <option value="update">Update</option>
                  <option value="alert">Alert</option>
                  <option value="claim">Claim</option>
                  <option value="match">Match</option>
                  <option value="verification">Verification</option>
                </select>
                <input
                  type="text"
                  placeholder="Action label (optional)"
                  value={bcActionLabel}
                  onChange={e => setBcActionLabel(e.target.value)}
                  style={{ flex: 1, padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181a20', color: '#fff', fontSize: 14, outline: 'none' }}
                />
              </div>
              <input
                type="url"
                placeholder="Action URL (optional)"
                value={bcActionUrl}
                onChange={e => setBcActionUrl(e.target.value)}
                style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181a20', color: '#fff', fontSize: 14, outline: 'none' }}
              />
              {bcError && <div style={{ color: '#ff5858', fontSize: 14 }}>{bcError}</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 18 }}>
              <button
                onClick={async () => {
                  setBcError("");
                  if (!bcMessage.trim()) { setBcError("Message is required"); return; }
                  setBcLoading(true);
                  try {
                    const headers = await getAuthHeaders({ "Content-Type": "application/json" });
                    const res = await fetch(getApiUrl("api/notifications/broadcast"), {
                      method: "POST",
                      headers,
                      body: JSON.stringify({
                        title: bcTitle || null,
                        message: bcMessage,
                        category: bcCategory || "announcement",
                        action_url: bcActionUrl || null,
                        action_label: bcActionLabel || null,
                      })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) {
                      throw new Error(data?.detail || "Failed to broadcast notification");
                    }
                    setFeedback({ open: true, type: 'success', message: `Broadcast sent to ${data?.created ?? 'all'} users` });
                    setBroadcastOpen(false);
                    setBcTitle(""); setBcMessage(""); setBcCategory("announcement"); setBcActionUrl(""); setBcActionLabel("");
                  } catch (e) {
                    setBcError(e.message || "Failed to broadcast notification");
                  }
                  setBcLoading(false);
                }}
                disabled={bcLoading}
                style={{ background: 'linear-gradient(90deg, #8E2DE2 0%, #4A00E0 100%)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 700, fontSize: 16, cursor: bcLoading ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px #4A00E055' }}
              >
                {bcLoading ? 'Sending…' : 'Send broadcast'}
              </button>
              <button
                onClick={() => { setBroadcastOpen(false); setBcError(""); }}
                disabled={bcLoading}
                style={{ background: '#23272f', color: '#aaa', border: '1.5px solid #444', borderRadius: 6, padding: '10px 24px', fontWeight: 700, fontSize: 16, cursor: bcLoading ? 'not-allowed' : 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    {/* Confirmation Dialog */}
    {confirmDialog.open && (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }}>
        <div style={{ background: '#23272f', borderRadius: 12, padding: 32, minWidth: 340, boxShadow: '0 4px 24px #0008', color: '#fff', textAlign: 'center' }}>
          <div style={{ fontSize: 18, marginBottom: 24 }}>{confirmDialog.message}</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
            <button onClick={handleConfirm} style={{ background: 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 32px', fontWeight: 700, fontSize: 16, cursor: 'pointer', boxShadow: '0 2px 8px #1976d255' }}>Confirm</button>
            <button onClick={() => setConfirmDialog({ open: false, uid: null, action: null, message: "", newRole: null })} style={{ background: '#23272f', color: '#aaa', border: '1.5px solid #444', borderRadius: 6, padding: '10px 32px', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      </div>
    )}

    {/* Admin Password Dialog */}
    {passwordDialog.open && (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
      }}>
        <div style={{ background: '#23272f', borderRadius: 12, padding: 32, minWidth: 340, boxShadow: '0 4px 24px #0008', color: '#fff', textAlign: 'center' }}>
          <div style={{ fontSize: 20, marginBottom: 18, fontWeight: 600 }}>Enter Admin Credentials</div>
          <input
            type="email"
            placeholder="Admin Email"
            value={adminEmail}
            onChange={e => setAdminEmail(e.target.value)}
            style={{ width: '100%', marginBottom: 16, padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181a20', color: '#fff', fontSize: 16, outline: 'none' }}
            autoFocus
          />
          <input
            type="password"
            placeholder="Admin Password"
            value={adminPassword}
            onChange={e => setAdminPassword(e.target.value)}
            style={{ width: '100%', marginBottom: 16, padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181a20', color: '#fff', fontSize: 16, outline: 'none' }}
          />
          {passwordError && <div style={{ color: '#ff5858', marginBottom: 12, fontSize: 15 }}>{passwordError}</div>}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 18 }}>
            <button
              onClick={() => performAction(adminEmail, adminPassword)}
              disabled={passwordLoading}
              style={{ background: 'linear-gradient(90deg, #1976d2 60%, #2196f3 100%)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 32px', fontWeight: 700, fontSize: 16, cursor: passwordLoading ? 'not-allowed' : 'pointer', boxShadow: '0 2px 8px #1976d255' }}
            >
              {passwordLoading ? 'Verifying...' : 'Confirm'}
            </button>
            <button
              onClick={() => { setPasswordDialog({ open: false, uid: null, action: null, newRole: null }); setPasswordError(""); setAdminEmail(""); setAdminPassword(""); }}
              disabled={passwordLoading}
              style={{ background: '#23272f', color: '#aaa', border: '1.5px solid #444', borderRadius: 6, padding: '10px 32px', fontWeight: 700, fontSize: 16, cursor: passwordLoading ? 'not-allowed' : 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default AdminDashboard;
